<script setup lang="ts">
import { AlignHorizontalCenters, AlignLeftBox, AlignRightBox } from '@iconoir/vue'
import { Pencil } from 'lucide-vue-next'
import type { Component } from 'vue'

import EmailLinkInput from '../ui/EmailLinkInput.vue'
import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import { LOGO_DEFAULT_WIDTH, LOGO_MAX_WIDTH, LOGO_MIN_WIDTH } from '../../utils/constants'
import type { EmailHeader, FooterAlignment } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  header: EmailHeader
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:header', header: EmailHeader): void
  (e: 'replace'): void
}>()

const updateField = <K extends keyof EmailHeader>(field: K, value: EmailHeader[K]) => {
  emit('update:header', { ...props.header, [field]: value })
}

const logoWidthValue = computed({
  get: () => [props.header.logoWidth ?? LOGO_DEFAULT_WIDTH],
  set: ([next]) => updateField('logoWidth', Math.max(LOGO_MIN_WIDTH, Math.min(LOGO_MAX_WIDTH, next)))
})

const alignments: { value: FooterAlignment; icon: Component }[] = [
  { value: 'left', icon: AlignLeftBox },
  { value: 'center', icon: AlignHorizontalCenters },
  { value: 'right', icon: AlignRightBox }
]
</script>

<template>
  <Teleport to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.content')">
        <Button type="button" variant="outline" class="w-full" @click="emit('replace')">
          <Pencil />
          {{ t('pages.email.emailTemplateCreatePage.changeImage') }}
        </Button>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="header.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => updateField('blockBackgroundColor', v)"
        />
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.globalAlignment')">
        <Tabs :model-value="header.align" @update:model-value="(v) => updateField('align', v as FooterAlignment)">
          <TabsList class="w-full">
            <TabsTrigger v-for="opt in alignments" :key="opt.value" :value="opt.value" class="flex-1">
              <component :is="opt.icon" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.logoSize')">
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <Label class="text-xs font-medium text-foreground">
              {{ t('pages.email.emailTemplateCreatePage.panel.imageWidth') }}
            </Label>
            <span class="text-[11px] tabular-nums text-muted-foreground">{{ logoWidthValue[0] }}px</span>
          </div>
          <Slider v-model="logoWidthValue" :min="LOGO_MIN_WIDTH" :max="LOGO_MAX_WIDTH" :step="10" />
        </div>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.link')">
        <EmailLinkInput
          stacked
          class="w-full"
          :model-value="header.link"
          @update:model-value="(v) => updateField('link', v)"
        />
      </EmailPanelSection>
    </div>
  </Teleport>
</template>
